import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { FileText, Plus, Trash2, Download, Sparkles, ChevronLeft, FileType2, Upload, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";

const fmtMoney = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(Number(n || 0));

const todayIso = new Date().toISOString().slice(0, 10);

const blankForm = () => ({
  customer: { company: "", address: "", contact_name: "", contact_position: "" },
  xentra: { contact_name: "", contact_position: "" },
  start_date: todayIso,
  term_months: 12,
  contract_no: "",
  licenses_qty: "",
  payment_frequency: "annual",
  billing_notes: "",
  line_items: [
    { description: "", qty: 1, unit_price_per_year: 0 },
  ],
});

export default function QuoteBuilder() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(blankForm());
  const [editingId, setEditingId] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/sales-tools/quotes?mine_only=true");
      setQuotes(r.data);
    } catch (_) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const loadForEdit = (q) => {
    setEditingId(q.id);
    setForm({
      customer: { ...(q.customer || {}) },
      xentra: { ...(q.xentra || {}) },
      start_date: q.start_date || todayIso,
      term_months: q.term_months || 12,
      contract_no: q.contract_no || "",
      licenses_qty: q.licenses_qty ?? "",
      payment_frequency: q.payment_frequency || "annual",
      billing_notes: q.billing_notes || "",
      line_items: (q.line_items && q.line_items.length ? q.line_items : [{ description: "", qty: 1, unit_price_per_year: 0 }])
        .map((li) => ({ description: li.description || "", qty: li.qty ?? 1, unit_price_per_year: li.unit_price_per_year ?? 0 })),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => { setEditingId(null); setForm(blankForm()); };

  const total = form.line_items.reduce(
    (s, li) =>
      s + Number(li.unit_price_per_year || 0) * Number(li.qty || 0) * (Number(form.term_months || 0) / 12),
    0,
  );

  const updateRoot = (k, v) => setForm({ ...form, [k]: v });
  const updateCust = (k, v) => setForm({ ...form, customer: { ...form.customer, [k]: v } });
  const updateXentra = (k, v) => setForm({ ...form, xentra: { ...form.xentra, [k]: v } });
  const updateItem = (i, k, v) => {
    const items = [...form.line_items];
    items[i] = { ...items[i], [k]: v };
    setForm({ ...form, line_items: items });
  };
  const addItem = () => setForm({
    ...form,
    line_items: [...form.line_items, { description: "", qty: 1, unit_price_per_year: 0 }],
  });
  const removeItem = (i) => setForm({
    ...form,
    line_items: form.line_items.filter((_, idx) => idx !== i),
  });

  const save = async () => {
    if (!form.customer.company.trim()) return toast.error("Customer company is required");
    if (!form.line_items.length || !form.line_items[0].description.trim())
      return toast.error("Add at least one line item");
    if (!form.term_months || form.term_months < 1 || form.term_months > 60)
      return toast.error("Term must be between 1 and 60 months");

    setSaving(true);
    try {
      const payload = {
        ...form,
        term_months: Number(form.term_months),
        licenses_qty: form.licenses_qty ? Number(form.licenses_qty) : null,
        billing_notes: (form.billing_notes || "").trim() || null,
        line_items: form.line_items.map((li) => ({
          description: li.description.trim(),
          qty: Number(li.qty) || 0,
          unit_price_per_year: Number(li.unit_price_per_year) || 0,
        })),
      };
      let r;
      if (editingId) {
        r = await api.patch(`/sales-tools/quotes/${editingId}`, payload);
        toast.success(`Quote updated · ${r.data.filename}`);
      } else {
        r = await api.post("/sales-tools/quotes", payload);
        toast.success(`Quote saved as ${r.data.filename}`);
      }
      // Immediately trigger PDF download
      await downloadPdf(r.data.id, r.data.filename);
      setEditingId(null);
      setForm(blankForm());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save quote");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (id, filename) => {
    try {
      const res = await api.get(`/sales-tools/quotes/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "work-order.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download PDF");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this quote?")) return;
    try {
      await api.delete(`/sales-tools/quotes/${id}`);
      await load();
      toast.success("Quote deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="quote-builder">
      <div className="flex items-center justify-between">
        <div>
          <Link
            to="/app/sales-tools"
            className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900 flex items-center gap-1"
            data-testid="back-to-sales-tools"
          >
            <ChevronLeft className="h-3 w-3" /> Sales Tools
          </Link>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-blue-600" /> Quotation Tool
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Fill in the details and generate a branded Customer Work Order PDF.
            File saves as <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">Customer_Product_Term_Month.pdf</code>.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* === Builder form (col-span-2) === */}
        <div className="lg:col-span-2 space-y-5">
          {/* Customer */}
          <Card className="border-slate-200">
            <CardHeader className="border-b border-slate-100 py-3">
              <CardTitle className="text-sm font-heading">Customer details</CardTitle>
            </CardHeader>
            <CardContent className="p-5 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Company *">
                  <Input data-testid="quote-company" value={form.customer.company} onChange={(e) => updateCust("company", e.target.value)} placeholder="e.g. CATS Global" />
                </Field>
                <Field label="Contract no.">
                  <Input data-testid="quote-contract-no" value={form.contract_no} onChange={(e) => updateRoot("contract_no", e.target.value)} placeholder="auto / XNT-2026-0042" />
                </Field>
              </div>
              <Field label="Address">
                <Textarea data-testid="quote-address" rows={2} value={form.customer.address} onChange={(e) => updateCust("address", e.target.value)} placeholder="Building, street, city, postcode" />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Contact name">
                  <Input data-testid="quote-contact-name" value={form.customer.contact_name} onChange={(e) => updateCust("contact_name", e.target.value)} />
                </Field>
                <Field label="Contact position">
                  <Input data-testid="quote-contact-position" value={form.customer.contact_position} onChange={(e) => updateCust("contact_position", e.target.value)} />
                </Field>
              </div>
            </CardContent>
          </Card>

          {/* Term & payment */}
          <Card className="border-slate-200">
            <CardHeader className="border-b border-slate-100 py-3">
              <CardTitle className="text-sm font-heading">Term &amp; payment</CardTitle>
            </CardHeader>
            <CardContent className="p-5 grid grid-cols-1 md:grid-cols-4 gap-3">
              <Field label="Start date *">
                <Input data-testid="quote-start-date" type="date" value={form.start_date} onChange={(e) => updateRoot("start_date", e.target.value)} />
              </Field>
              <Field label="Term (months) *">
                <Select value={String(form.term_months)} onValueChange={(v) => updateRoot("term_months", Number(v))}>
                  <SelectTrigger data-testid="quote-term"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {Array.from({ length: 60 }, (_, i) => i + 1).map((m) => (
                      <SelectItem key={m} value={String(m)}>{m} {m === 1 ? "month" : "months"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Payment freq.">
                <Select value={form.payment_frequency} onValueChange={(v) => updateRoot("payment_frequency", v)}>
                  <SelectTrigger data-testid="quote-payment-freq"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="upfront">Upfront</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Total licenses">
                <Input data-testid="quote-licenses" type="number" value={form.licenses_qty} onChange={(e) => updateRoot("licenses_qty", e.target.value)} placeholder="optional" />
              </Field>
            </CardContent>
          </Card>

          {/* Line items */}
          <Card className="border-slate-200">
            <CardHeader className="border-b border-slate-100 py-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-heading">Section A · Line items</CardTitle>
              <Button variant="outline" size="sm" onClick={addItem} data-testid="add-line-item">
                <Plus className="h-3 w-3 mr-1" /> Add line
              </Button>
            </CardHeader>
            <CardContent className="p-5 space-y-3">
              {form.line_items.map((li, i) => {
                const lineTotal = Number(li.qty || 0) * Number(li.unit_price_per_year || 0) * (Number(form.term_months || 0) / 12);
                return (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`line-item-${i}`}>
                    <div className="col-span-6">
                      <label className="text-[10px] font-mono uppercase text-slate-500">Description</label>
                      <Input
                        data-testid={`line-desc-${i}`}
                        value={li.description}
                        onChange={(e) => updateItem(i, "description", e.target.value)}
                        placeholder="Securly Web Filter / Barracuda WAF / etc."
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-[10px] font-mono uppercase text-slate-500">Qty</label>
                      <Input
                        data-testid={`line-qty-${i}`}
                        type="number" min={1}
                        value={li.qty}
                        onChange={(e) => updateItem(i, "qty", e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-[10px] font-mono uppercase text-slate-500">Unit £/yr</label>
                      <Input
                        data-testid={`line-unit-${i}`}
                        type="number" step="0.01"
                        value={li.unit_price_per_year}
                        onChange={(e) => updateItem(i, "unit_price_per_year", e.target.value)}
                      />
                    </div>
                    <div className="col-span-1 text-right">
                      <div className="text-[10px] font-mono uppercase text-slate-500">Total</div>
                      <div className="text-sm font-mono font-semibold text-emerald-700">{fmtMoney(lineTotal)}</div>
                    </div>
                    <div className="col-span-1 text-right">
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => removeItem(i)}
                        disabled={form.line_items.length === 1}
                        data-testid={`line-remove-${i}`}
                      >
                        <Trash2 className="h-4 w-4 text-slate-400 hover:text-rose-600" />
                      </Button>
                    </div>
                  </div>
                );
              })}
              <div className="border-t border-slate-200 pt-3 flex items-center justify-between">
                <span className="text-xs font-mono uppercase text-slate-500">Grand total ({form.term_months} months)</span>
                <span className="text-2xl font-mono font-bold text-emerald-700" data-testid="quote-grand-total">
                  {fmtMoney(total)}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Section B · Billing notes */}
          <Card className="border-slate-200">
            <CardHeader className="border-b border-slate-100 py-3">
              <CardTitle className="text-sm font-heading">Section B · Billing notes</CardTitle>
            </CardHeader>
            <CardContent className="p-5">
              <label className="text-[10px] font-mono uppercase text-slate-500">
                Notes (appear directly under the payment schedule)
              </label>
              <Textarea
                data-testid="quote-billing-notes"
                rows={4}
                value={form.billing_notes}
                onChange={(e) => updateRoot("billing_notes", e.target.value)}
                placeholder="e.g. Invoiced in advance on the anniversary. First invoice raised on signature. PO reference: XYZ."
              />
              <div className="text-[10px] text-slate-500 mt-1">
                Free text — line breaks are preserved on the PDF. Leave blank to omit.
              </div>
            </CardContent>
          </Card>

          {/* Xentra signatory */}
          <Card className="border-slate-200">
            <CardHeader className="border-b border-slate-100 py-3">
              <CardTitle className="text-sm font-heading">Xentra signatory (page 2)</CardTitle>
            </CardHeader>
            <CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Name">
                <Input data-testid="quote-xentra-name" value={form.xentra.contact_name} onChange={(e) => updateXentra("contact_name", e.target.value)} placeholder="e.g. Henry Doyle" />
              </Field>
              <Field label="Position">
                <Input data-testid="quote-xentra-position" value={form.xentra.contact_position} onChange={(e) => updateXentra("contact_position", e.target.value)} placeholder="e.g. CEO" />
              </Field>
            </CardContent>
          </Card>

          <div className="flex items-center justify-between gap-3">
            <div>
              {editingId && (
                <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider px-2 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800" data-testid="edit-mode-banner">
                  <Pencil className="h-3 w-3" /> Editing existing quote
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={editingId ? cancelEdit : () => setForm(blankForm())} data-testid="quote-reset">
                {editingId ? "Cancel edit" : "Reset"}
              </Button>
              <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="quote-save">
                <Download className="h-4 w-4 mr-1" />
                {saving ? "Generating…" : editingId ? "Save & regenerate PDF" : "Generate PDF"}
              </Button>
            </div>
          </div>
        </div>

        {/* === My quotes (right column) === */}
        <div className="space-y-4">
          <Card className="border-slate-200">
            <CardHeader className="border-b border-slate-100 py-3">
              <CardTitle className="text-sm font-heading">My quotes ({quotes.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0 max-h-[80vh] overflow-y-auto">
              {quotes.length === 0 ? (
                <div className="text-xs text-slate-400 text-center py-8 px-4">
                  Your saved quotes will appear here.
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {quotes.map((q) => (
                    <li key={q.id} className={`p-3 hover:bg-slate-50 ${editingId === q.id ? "bg-amber-50/60" : ""}`} data-testid={`saved-quote-${q.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">{q.customer?.company}</div>
                          <div className="text-[11px] font-mono text-slate-500 truncate">{q.filename}</div>
                          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                            <Badge variant="outline" className="text-[10px]">{q.term_months}m</Badge>
                            <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                              {fmtMoney(q.grand_total)}
                            </Badge>
                            <span className="text-[10px] text-slate-400 font-mono">
                              {q.created_at?.slice(0, 10)}
                            </span>
                            {q.updated_at && q.updated_at !== q.created_at && (
                              <span className="text-[10px] text-amber-700 font-mono">edited {q.updated_at?.slice(0, 10)}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button variant="ghost" size="icon" onClick={() => loadForEdit(q)} title="Edit" data-testid={`edit-quote-${q.id}`}>
                            <Pencil className="h-4 w-4 text-slate-500 hover:text-amber-600" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => downloadPdf(q.id, q.filename)} title="Download PDF" data-testid={`download-quote-${q.id}`}>
                            <Download className="h-4 w-4 text-slate-500 hover:text-blue-600" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => remove(q.id)} title="Delete" data-testid={`delete-quote-${q.id}`}>
                            <Trash2 className="h-4 w-4 text-slate-400 hover:text-rose-600" />
                          </Button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="border-blue-100 bg-blue-50/40">
            <CardContent className="p-5">
              <div className="flex items-start gap-2">
                <FileText className="h-4 w-4 text-blue-700 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-slate-700 leading-relaxed">
                  Generated PDFs use Xentra branding and include the standard T&amp;Cs preamble plus the
                  Order Confirmation signature block on page 2. Files are saved as
                  <code className="bg-white border border-slate-200 px-1 rounded mx-1">
                    Customer_Product_Term_Month.pdf
                  </code>.
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase text-slate-500">{label}</label>
      {children}
    </div>
  );
}
